import React, { useRef, useState } from 'react';

import { useDispatch } from 'react-redux';

import Tooltip from '@mui/material/Tooltip';
import ArrowIcon from 'assets/LeadInfo/arrow.svg';
import ClockIcon from 'assets/LeadInfo/clock.svg';
import DeleteIcon from 'assets/LeadInfo/delete.svg';
import EditIcon from 'assets/LeadInfo/edit.svg';
import BadgeIcon from 'assets/LeadInfo/package.svg';
import ShoppingIcon from 'assets/LeadInfo/shopping.svg';
import TagIcon from 'assets/LeadInfo/tag.svg';
import Clock from 'assets/clock.svg';
import Button from 'common-ui/button';
import Modal from 'common-ui/modal';
import {
  approveOrderOffer,
  deleteLeadOffer,
  deleteOrderOffer,
  rejectOrderOffer,
} from 'features/newsales/newsalesActions';
import { formatDateTime } from 'utils/dateUtils';

import AddOfferModal from '../addOfferModal';
import CreateOrderModal from '../createOrderModal';
import {
  ActionButton,
  ArrowWrapper,
  BoxDiscount,
  BoxLeftContent,
  BoxLeftImg,
  BoxPrice,
  BtnIcon,
  CancelButton,
  CardMain,
  CardMainBox,
  CategoryBadge,
  CategoryBadgeIcon,
  ClockImg,
  ConfirmActions,
  ConfirmMessage,
  DateText,
  DateWrapper,
  EmptyMessage,
  FlexLeft,
  FlexRight,
  FlexTitle,
  HeaderTitle,
  HistoryIcon,
  LeftContentText,
  LeftContentTitle,
  MainBoxLeft,
  MainBoxRight,
  OfferCard,
  OfferContent,
  OfferHeader,
  OfferIcon,
  OrderBlock,
  OrderBlockBox,
  OrderDate,
  OrderDetails,
  OrderEdit,
  OrderFlex,
  OrderHistory,
  OrderHistoryBox,
  OrderName,
  OrderOfferDesc,
  OrderTags,
  OrderTitle,
  StatusDropdown,
  StatusDropdownItem,
  StatusDropdownWrapper,
  SubmitButton,
  TagItem,
} from './OfferInfo.styles';

const OfferInfo = ({ leadData, onUpdate }) => {
  const dispatch = useDispatch();
  const [openOrderId, setOpenOrderId] = useState(null);
  const [isCreateOrderOpen, setIsCreateOrderOpen] = useState(false);
  const [isAddOfferOpen, setIsAddOfferOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);

  const offers = leadData?.offers || [];
  const dropdownRef = useRef(null);

  const truncateText = (text, maxLength = 30) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const toggleOrders = (id) => {
    setOpenOrderId(openOrderId === id ? null : id);
  };

  const toggleStatusDropdown = (orderId) => {
    setStatusDropdownOpen(statusDropdownOpen === orderId ? null : orderId);
  };

  const handleOpenCreateOrder = (offer) => {
    setSelectedOffer(offer);
    setEditingOrder(null);
    setIsCreateOrderOpen(true);
  };

  const handleOpenEditOrder = (offer, order) => {
    setSelectedOffer(offer);
    setEditingOrder(order);
    setIsCreateOrderOpen(true);
  };

  const handleOrderSuccess = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  const handleDeleteOrder = async (offerId, orderId) => {
    try {
      await dispatch(
        deleteOrderOffer({
          leadId: leadData?.id,
          orderOfferId: orderId,
        })
      ).unwrap();
      setConfirmAction(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to delete order:', error);
    }
  };

  const handleApproveOrder = async (offerId, orderId) => {
    try {
      await dispatch(
        approveOrderOffer({
          leadId: leadData?.id,
          orderOfferId: orderId,
        })
      ).unwrap();
      setConfirmAction(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to approve order:', error);
    }
  };

  const handleRejectOrder = async (offerId, orderId) => {
    try {
      await dispatch(
        rejectOrderOffer({
          leadId: leadData?.id,
          orderOfferId: orderId,
        })
      ).unwrap();
      setConfirmAction(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Failed to reject order:', error);
    }
  };

  const handleDeleteOffer = async (offerId) => {
    try {
      setConfirmLoading(true);
      await dispatch(deleteLeadOffer({ leadId: leadData?.id, offerId })).unwrap();
      setConfirmAction(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      setConfirmLoading(false);
    }
  };

  const handleAddOfferSuccess = () => {
    if (onUpdate) {
      onUpdate();
    }
  };

  return (
    <OfferContent>
      <OfferHeader>
        <HeaderTitle>
          <OfferIcon src={TagIcon} />
          Offers
        </HeaderTitle>
        <Button secondary className="add-button" onClick={() => setIsAddOfferOpen(true)}>
          <span>+</span> Add Offer
        </Button>
      </OfferHeader>
      {offers.length === 0 ? (
        <EmptyMessage>
          <div>No offers available yet</div>
          <div style={{ fontSize: '13px', color: '#94a3b8' }}>
            Click &ldquo;Add Offer&rdquo;
          </div>
        </EmptyMessage>
      ) : (
        offers.map((offer) => {
          const isOpen = openOrderId === offer.id;
          const hasOrders = offer.orders && offer.orders.length > 0;
          return (
            <OfferCard key={offer.id}>
              <CardMain>
                <CardMainBox>
                  <MainBoxLeft>
                    {offer.image && <BoxLeftImg src={offer.image} />}
                    <BoxLeftContent>
                      <Tooltip
                        title={offer.name?.length > 30 ? offer.name : ''}
                        placement="top"
                        arrow
                      >
                        <LeftContentTitle>{truncateText(offer.name, 30)}</LeftContentTitle>
                      </Tooltip>
                      {offer.description && (
                        <Tooltip
                          title={offer.description?.length > 30 ? offer.description : ''}
                          placement="top"
                          arrow
                        >
                          <LeftContentText>
                            {truncateText(offer.description, 30)}
                          </LeftContentText>
                        </Tooltip>
                      )}
                      {offer.salesHelpText && (
                        <Tooltip
                          title={offer.salesHelpText?.length > 30 ? offer.salesHelpText : ''}
                          placement="top"
                          arrow
                        >
                          <LeftContentText>
                            {truncateText(offer.salesHelpText, 30)}
                          </LeftContentText>
                        </Tooltip>
                      )}
                      {offer.premiumFeatures && Array.isArray(offer.premiumFeatures) && (
                        <CategoryBadge>
                          <CategoryBadgeIcon src={BadgeIcon} />
                          {offer.premiumFeatures.map((feature, index) => (
                            <span key={index} className="feature-item">
                              {feature}
                            </span>
                          ))}
                        </CategoryBadge>
                      )}
                    </BoxLeftContent>
                  </MainBoxLeft>
                  <MainBoxRight>
                    {offer.discountedPrice && (
                      <BoxPrice>
                        {offer.price} {offer.currency}
                      </BoxPrice>
                    )}
                    <BoxDiscount className={!offer.discountedPrice ? 'black' : ''}>
                      {offer.discountedPrice || offer.price} {offer.currency}
                    </BoxDiscount>
                  </MainBoxRight>
                </CardMainBox>

                <OrderBlockBox>
                  <OrderBlock>
                    <Button
                      onClick={() => handleOpenCreateOrder(offer)}
                      secondary
                      className="view-orders-btn create-orders-btn"
                    >
                      <BtnIcon src={ShoppingIcon} />
                      Create Order
                    </Button>
                    {hasOrders && (
                      <Button
                        onClick={() => toggleOrders(offer.id)}
                        className="view-orders-btn"
                      >
                        <ArrowWrapper isOpen={isOpen}>
                          <img src={ArrowIcon} alt="arrow" />
                        </ArrowWrapper>
                        View Orders ({offer.orders.length})
                      </Button>
                    )}
                  </OrderBlock>
                  <Button
                    className="shopping-btn"
                    onClick={() =>
                      setConfirmAction({
                        type: 'deleteOffer',
                        offerId: offer.id,
                      })
                    }
                  >
                    <BtnIcon src={DeleteIcon} />
                    Delete
                  </Button>
                </OrderBlockBox>
                {(offer.startTime || offer.endTime) && (
                  <DateWrapper>
                    <ClockImg src={Clock} />
                    <DateText style={{ color: '#6C757D' }}>Validity Period</DateText>
                    <DateText>
                      {offer.startTime &&
                        `From: ${formatDateTime(offer.startTime, false, false, 'MMM D,YYYY')}`}
                      {offer.startTime && offer.endTime && ' | '}
                      {offer.endTime &&
                        `To: ${formatDateTime(offer.endTime, false, false, 'MMM D,YYYY')}`}
                    </DateText>
                  </DateWrapper>
                )}
              </CardMain>

              {hasOrders && (
                <OrderDetails isOpen={isOpen}>
                  <OrderTitle>
                    Orders <span>({offer.orders.length})</span>
                  </OrderTitle>
                  {offer.orders.map((order, index) => {
                    const orderStatus = order.status || 'Pending';
                    const isPending = orderStatus.toLowerCase() === 'pending';
                    const statusLower = orderStatus.toLowerCase();
                    return (
                      <OrderFlex key={index}>
                        <FlexLeft>
                          <FlexTitle>{offer.name}</FlexTitle>
                          {order.description && (
                            <OrderOfferDesc>{order.description}</OrderOfferDesc>
                          )}
                          {order.preferredDate && (
                            <OrderDate style={{ marginBottom: '8px' }}>
                              <ClockImg
                                src={ClockIcon}
                                style={{ width: '14px', marginRight: '4px' }}
                              />
                              Preferred Date:{' '}
                              {formatDateTime(
                                order.preferredDate,
                                false,
                                false,
                                'MMM D, YYYY'
                              )}
                            </OrderDate>
                          )}
                          <OrderTags>
                            <StatusDropdownWrapper ref={dropdownRef}>
                              <TagItem
                                $status={statusLower}
                                $clickable={isPending}
                                onClick={() => isPending && toggleStatusDropdown(order.id)}
                              >
                                {orderStatus}
                              </TagItem>
                              {isPending && statusDropdownOpen === order.id && (
                                <StatusDropdown>
                                  <StatusDropdownItem
                                    $variant="approve"
                                    onClick={() => {
                                      setConfirmAction({
                                        type: 'approve',
                                        offerId: offer.id,
                                        orderId: order.id,
                                      });
                                      setStatusDropdownOpen(null);
                                    }}
                                  >
                                    Approve
                                  </StatusDropdownItem>
                                  <StatusDropdownItem
                                    $variant="reject"
                                    onClick={() => {
                                      setConfirmAction({
                                        type: 'reject',
                                        offerId: offer.id,
                                        orderId: order.id,
                                      });
                                      setStatusDropdownOpen(null);
                                    }}
                                  >
                                    Reject
                                  </StatusDropdownItem>
                                </StatusDropdown>
                              )}
                            </StatusDropdownWrapper>
                            {order.count && (
                              <TagItem className="qty">Qty: {order.count}</TagItem>
                            )}
                          </OrderTags>
                        </FlexLeft>
                        <FlexRight>
                          <BoxDiscount>
                            {(
                              (offer.discountedPrice || offer.price) * order.count
                            ).toLocaleString()}{' '}
                            {offer.currency}
                          </BoxDiscount>
                          {order.createdAt && (
                            <OrderDate>
                              {new Date(order.createdAt).toLocaleDateString()}
                              {order.createdBy && (
                                <OrderName>
                                  <span>By:</span>{' '}
                                  {order.createdBy.name && order.createdBy.surname
                                    ? `${order.createdBy.name} ${order.createdBy.surname}`
                                    : order.createdBy.name || order.createdBy.email || 'N/A'}
                                </OrderName>
                              )}
                            </OrderDate>
                          )}
                          <OrderHistoryBox>
                            <OrderHistory>
                              <HistoryIcon src={ClockIcon} />
                              Show history
                            </OrderHistory>
                            <OrderEdit onClick={() => handleOpenEditOrder(offer, order)}>
                              <HistoryIcon src={EditIcon} />
                              Edit
                            </OrderEdit>
                            <ActionButton
                              onClick={() =>
                                setConfirmAction({
                                  type: 'delete',
                                  offerId: offer.id,
                                  orderId: order.id,
                                })
                              }
                            >
                              <HistoryIcon src={DeleteIcon} />
                              Delete
                            </ActionButton>
                          </OrderHistoryBox>
                        </FlexRight>
                      </OrderFlex>
                    );
                  })}
                </OrderDetails>
              )}
            </OfferCard>
          );
        })
      )}
      <AddOfferModal
        isOpen={isAddOfferOpen}
        onClose={() => setIsAddOfferOpen(false)}
        leadData={leadData}
        onSuccess={handleAddOfferSuccess}
      />

      <CreateOrderModal
        isOpen={isCreateOrderOpen}
        onClose={() => {
          setIsCreateOrderOpen(false);
          setEditingOrder(null);
        }}
        offer={selectedOffer}
        leadId={leadData?.id}
        onSuccess={handleOrderSuccess}
        orderOffer={editingOrder}
      />

      <Modal
        isOpen={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        title={`Confirm ${
          confirmAction?.type === 'deleteOffer'
            ? 'Delete Offer'
            : confirmAction?.type === 'delete'
              ? 'Delete Order'
              : confirmAction?.type === 'approve'
                ? 'Approve Order'
                : 'Reject Order'
        }`}
        footer={false}
        width="400px"
      >
        <ConfirmMessage>
          {confirmAction?.type === 'approve' || confirmAction?.type === 'reject'
            ? "Are you sure you want to update this order's status?"
            : `Are you sure you want to ${
                confirmAction?.type === 'deleteOffer'
                  ? 'delete this offer'
                  : 'delete this order'
              }? This action cannot be undone.`}
        </ConfirmMessage>
        <ConfirmActions>
          <CancelButton onClick={() => setConfirmAction(null)} disabled={confirmLoading}>
            Cancel
          </CancelButton>
          <SubmitButton
            disabled={confirmLoading}
            onClick={() => {
              if (confirmAction?.type === 'deleteOffer') {
                handleDeleteOffer(confirmAction.offerId);
              } else if (confirmAction?.type === 'delete') {
                handleDeleteOrder(confirmAction.offerId, confirmAction.orderId);
              } else if (confirmAction?.type === 'approve') {
                handleApproveOrder(confirmAction.offerId, confirmAction.orderId);
              } else {
                handleRejectOrder(confirmAction.offerId, confirmAction.orderId);
              }
            }}
          >
            {confirmLoading
              ? 'Processing...'
              : confirmAction?.type === 'deleteOffer'
                ? 'Delete Offer'
                : confirmAction?.type === 'delete'
                  ? 'Delete Order'
                  : confirmAction?.type === 'approve'
                    ? 'Approve Order'
                    : 'Reject Order'}
          </SubmitButton>
        </ConfirmActions>
      </Modal>
    </OfferContent>
  );
};

export default OfferInfo;
